import { SOURCE_LABELS } from '../../api/types';
import type { SourceHealth } from '../../api/types';
import { Badge } from '../../components/Badge';
import type { Tone } from '../../components/Badge';
import { Card } from '../../components/Card';
import { useFormatters } from '../../time/format';
import { formatDuration, millisUntil, useNow } from '../../time/useNow';

export const STATE_TONES: Record<SourceHealth['state'], Tone> = {
  ok: 'success',
  // Configured, credentialled, and not heard from in four poll cycles. Amber rather than red
  // because nothing has reported an error — which is exactly what makes it worth showing.
  stale: 'warning',
  // Never polled. On a fresh deployment this is every source for the first few minutes, so it
  // is not a fault; it stops being true on its own or turns into `stale`.
  pending: 'info',
  // Not an error. A source with no secret is skipped and reported, so listing one before its
  // credentials exist is safe — colouring it red would train operators to ignore red.
  unconfigured: 'neutral',
  failed: 'danger',
};

interface SourcePulseProps {
  source: SourceHealth;
  /** The configured cadence, which is what a full ring represents. */
  intervalSeconds: number;
  /** Narrows the log console and the run history to this source. */
  onSelect: (source: string) => void;
  selected: boolean;
}

/**
 * One source's card: state, a countdown to its next poll, and what its last poll did.
 *
 * The countdown is the part worth arguing for. A timestamp saying a poll succeeded four
 * minutes ago requires the reader to know the cadence, subtract, and decide whether four
 * minutes is fine — and the answer is different for each source. A ring draining towards zero
 * answers it without arithmetic, and a ring that reaches zero and stays there is the clearest
 * possible statement that the schedule has stopped, which is a fault this platform has
 * actually had and which no single timestamp on the old page made visible.
 */
export function SourcePulse({ source, intervalSeconds, onSelect, selected }: SourcePulseProps) {
  const { relative, precise } = useFormatters();
  const now = useNow();

  const label = SOURCE_LABELS[source.source] ?? source.source;
  const remaining = millisUntil(source.next_poll_at, now);
  const run = source.last_run;

  return (
    <Card
      className={`flex flex-col gap-3 ${selected ? 'ring-2 ring-brand/50' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => onSelect(selected ? '' : source.source)}
          // The card filters the console and the history below it, so it has to be operable
          // from the keyboard. Only the heading is the control: making the whole card a
          // button would nest the state badge inside it.
          className="min-w-0 text-left font-semibold text-body hover:text-brand-text"
          aria-pressed={selected}
        >
          <span className="truncate">{label}</span>
        </button>
        <Badge tone={STATE_TONES[source.state]} emphasis={source.state === 'failed'}>
          {source.state}
        </Badge>
      </div>

      {/*
        Three cases, and the order matters. An unconfigured source is checked first because
        several of them are also webhook-only, and "Webhook only — silence is not evidence" is
        a claim about a transport that is working. For a source with no credential, or none
        with an adapter at all, that reads as a feed that is fine and quiet.
      */}
      {source.state === 'unconfigured' ? (
        <Note
          title="Not configured"
          detail={
            source.last_error ||
            'No credential, so nothing polls it and no delivery is expected yet.'
          }
        />
      ) : source.pollable ? (
        <PollClock
          remainingMs={remaining}
          intervalSeconds={intervalSeconds}
          stalled={source.state === 'stale' || source.state === 'failed'}
        />
      ) : (
        <Note
          title="Webhook only"
          detail="Nothing is scheduled, so silence here is not evidence either way."
        />
      )}

      <dl className="space-y-1 border-t border-line pt-2 text-xs">
        <Row
          label="Last success"
          value={
            source.last_success ? (
              <span title={precise(source.last_success)}>{relative(source.last_success)}</span>
            ) : (
              'never'
            )
          }
        />
        {run ? (
          <>
            <Row
              label="Last run"
              value={
                <span className={run.ok ? 'text-success-fg' : 'text-danger-fg'}>
                  {run.ok ? 'ok' : 'failed'} · {formatDuration(run.duration_ms)}
                </span>
              }
            />
            <Row
              label="Ingested"
              value={`${run.ingested} from ${run.pages} ${run.pages === 1 ? 'page' : 'pages'}`}
            />
          </>
        ) : source.pollable ? (
          <Row label="Last run" value="no run recorded yet" />
        ) : null}
      </dl>

      {/*
        Suppressed for an unconfigured source, whose `last_error` is an explanation rather
        than a fault — "no adapter is implemented for this source yet" is not something that
        went wrong. The note above already carries it, and a red box around it would train
        operators to ignore red, which is the same reason the badge tone is neutral.
      */}
      {source.last_error && source.state !== 'unconfigured' ? (
        <p className="break-words rounded-lg bg-danger-bg px-2 py-1.5 font-mono text-[11px] text-danger-fg">
          {source.last_error}
        </p>
      ) : null}
    </Card>
  );
}

function Note({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="flex h-[76px] flex-col justify-center">
      <p className="text-sm font-medium text-muted">{title}</p>
      <p className="mt-0.5 text-xs text-subtle">{detail}</p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-subtle">{label}</dt>
      <dd className="truncate text-right tabular-nums text-muted">{value}</dd>
    </div>
  );
}

const RADIUS = 30;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

interface PollClockProps {
  remainingMs: number | null;
  intervalSeconds: number;
  stalled: boolean;
}

/**
 * The ring, and the number inside it.
 *
 * Drawn from `remainingMs` against the cadence rather than from a stored fraction, so a tab
 * that was in the background for ten minutes shows the right arc on the first frame after it
 * is focused instead of animating from wherever a counter stopped.
 *
 * A negative remainder is rendered as overdue rather than clamped to zero. Clamping turns
 * "this schedule stopped firing an hour ago" into "due now", which is the one reading this
 * component exists to prevent.
 */
function PollClock({ remainingMs, intervalSeconds, stalled }: PollClockProps) {
  if (remainingMs === null) {
    return (
      <div className="flex h-[76px] items-center gap-3">
        <Ring fraction={0} tone="neutral" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-muted">Next poll unknown</p>
          <p className="text-xs text-subtle">
            No poll recorded, so the schedule's phase cannot be derived.
          </p>
        </div>
      </div>
    );
  }

  const overdue = remainingMs < 0;
  const interval = Math.max(intervalSeconds, 1) * 1000;
  // Clamped so a countdown longer than the interval — which happens for a few seconds after
  // the cadence is widened — draws a full ring rather than overflowing it.
  const fraction = Math.min(Math.max(remainingMs / interval, 0), 1);
  const tone = overdue ? (stalled ? 'danger' : 'warning') : 'brand';

  return (
    <div className="flex h-[76px] items-center gap-3">
      <Ring fraction={overdue ? 1 : fraction} tone={tone} />
      <div className="min-w-0">
        <p
          className={`text-xl font-bold tabular-nums ${
            overdue ? (stalled ? 'text-danger-fg' : 'text-warning-fg') : 'text-body'
          }`}
        >
          {overdue ? formatDuration(-remainingMs) : formatDuration(remainingMs)}
        </p>
        <p className="text-xs text-subtle">
          {overdue ? 'overdue — schedule has not fired' : 'until next scheduled poll'}
        </p>
      </div>
    </div>
  );
}

const RING_STROKES: Record<'brand' | 'warning' | 'danger' | 'neutral', string> = {
  brand: 'stroke-brand',
  warning: 'stroke-warning-fg',
  danger: 'stroke-danger-fg',
  neutral: 'stroke-line-strong',
};

function Ring({
  fraction,
  tone,
}: {
  fraction: number;
  tone: keyof typeof RING_STROKES;
}) {
  return (
    <svg viewBox="0 0 72 72" className="h-[68px] w-[68px] shrink-0 -rotate-90" aria-hidden="true">
      <circle cx="36" cy="36" r={RADIUS} fill="none" strokeWidth="6" className="stroke-line" />
      <circle
        cx="36"
        cy="36"
        r={RADIUS}
        fill="none"
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={CIRCUMFERENCE * (1 - fraction)}
        className={RING_STROKES[tone]}
      />
    </svg>
  );
}
